import { timing, variantForLevel } from './params';
/** 9 symboles canoniques (Unicode, mobile-friendly). Index 0-8 → chiffre 1-9. */
export const SYMBOLS_KEY = [
    '◇',
    '○',
    '△',
    '□',
    '✕',
    '☆',
    '▽',
    '◐',
    '✓',
];
const N_SYMBOLS = SYMBOLS_KEY.length;
const N_OPTIONS = 4;
/** Helper : pour un digit (1-9), retourne le symbole correspondant via la key passée. */
export function digitToSymbol(digit, key) {
    const d = Math.max(1, Math.min(N_SYMBOLS, digit));
    const symIdx = key[d - 1];
    return SYMBOLS_KEY[symIdx ?? 0];
}
/** Génère une key déterministe : digit (1-9) → index dans SYMBOLS_KEY. */
function makeKey() {
    // Permutation aléatoire de [0..8].
    const arr = Array.from({ length: N_SYMBOLS }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr;
}
/**
 * Construit un stimulus pour un trial.
 *   - choisit un digit 1-9 aléatoire
 *   - calcule l'index symbole correct via la key
 *   - tire 3 distracteurs uniques différents du correct
 *   - shuffle les 4 options
 */
export function makeStimulus(trialIdx, value, level, key) {
    const variant = variantForLevel(level);
    // Le pool de digits actifs dépend du nPairs courant (6 → 9).
    const activeDigits = Math.max(2, Math.min(N_SYMBOLS, variant.nPairs));
    const digit = 1 + Math.floor(Math.random() * activeDigits);
    const correctSymbolIdx = key[digit - 1];
    // Tire 3 distracteurs uniques parmi les indices [0..activeDigits-1] ≠ correct.
    const distractorPool = [];
    for (let d = 0; d < activeDigits; d += 1) {
        const idx = key[d];
        if (idx !== correctSymbolIdx)
            distractorPool.push(idx);
    }
    // Shuffle pool + prend 3 premiers.
    for (let i = distractorPool.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = distractorPool[i];
        distractorPool[i] = distractorPool[j];
        distractorPool[j] = tmp;
    }
    const distractors = distractorPool.slice(0, Math.min(N_OPTIONS - 1, distractorPool.length));
    const options = [correctSymbolIdx, ...distractors];
    // Shuffle final.
    for (let i = options.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = options[i];
        options[i] = options[j];
        options[j] = tmp;
    }
    return {
        trialIdx,
        value: Math.max(timing.feedbackMs, value),
        digit,
        correctSymbolIdx,
        options,
    };
}
export function createSymbolCodingAdapter(level = 1) {
    const key = makeKey();
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        key,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(pickedSymbolIdx, stim) {
        if (!resolver)
            return;
        const isTimeout = pickedSymbolIdx === -1;
        const rt = isTimeout ? stim.value : Date.now() - trialStart;
        const correct = !isTimeout && pickedSymbolIdx === stim.correctSymbolIdx;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ picked: pickedSymbolIdx, correct, reactionTimeMs: rt });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    // Petit délai d'affichage du digit avant ouverture des options.
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        trialStart = Date.now();
                        // Timeout fenêtre réponse.
                        timers.push(setTimeout(() => {
                            settle(-1, stim);
                        }, stim.value));
                    }, 150));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleAnswer(pickedSymbolIdx) {
            if (state.subPhase !== 'awaiting_input')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(pickedSymbolIdx, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
        getKey() {
            return state.key;
        },
    };
}
//# sourceMappingURL=adapter.js.map